import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardActionArea,
  Rating,
  CircularProgress,
  Chip,
  Button,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { getAllProducts } from "../../../../../services/Public/PublicPagesApi";
import { getImageUrl } from "../../../../../services/indexMediaBaseUrl";

const TopRatedSection = () => {
  const { t, i18n } = useTranslation();
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const MAX_ITEMS = 4;

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await getAllProducts();
        if (res && res.success && Array.isArray(res.products)) {
          const flat = [];
          res.products.forEach((entry) => {
            if (Array.isArray(entry.products)) {
              flat.push(...entry.products);
            }
          });
          // sort by ratings_count score desc, then by total_reviews desc, then by average_rating desc
          flat.sort((a, b) => {
            const targetRating = 3.6;
            const aAvg = a.reviews_summary?.average_rating || 0;
            const bAvg = b.reviews_summary?.average_rating || 0;

            const aIsTarget = Math.abs(aAvg - targetRating) < 1e-6;
            const bIsTarget = Math.abs(bAvg - targetRating) < 1e-6;
            if (aIsTarget && !bIsTarget) return -1;
            if (bIsTarget && !aIsTarget) return 1;

            const aCounts = a.reviews_summary?.ratings_count || {};
            const bCounts = b.reviews_summary?.ratings_count || {};
            const ratings = [1, 2, 3, 4, 5];
            const sumScore = (counts) =>
              ratings.reduce((s, r) => {
                const key = String(r);
                const cnt = Number(counts[key] ?? 0);
                return s + cnt * r;
              }, 0);

            const aScore = sumScore(aCounts);
            const bScore = sumScore(bCounts);
            if (bScore !== aScore) return bScore - aScore;

            const aTotal = a.reviews_summary?.total_reviews || 0;
            const bTotal = b.reviews_summary?.total_reviews || 0;
            if (bTotal !== aTotal) return bTotal - aTotal;

            return bAvg - aAvg;
          });
          setProducts(flat.slice(0, MAX_ITEMS));
        } else {
          setProducts([]);
        }
      } catch (err) {
        setError(err.message || "Error");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const getName = (p) => (lang === "ar" ? p.name_ar || p.name_en : p.name_en || p.name_ar);
  const getImage = (p) => getImageUrl(p.main_photo || (p.images && p.images[0] && p.images[0].image) || "");
  // helpers: pick price entry by stored country_id and format
  const getSelectedCountryId = () => {
    try {
      const v = localStorage.getItem("country_id");
      return v !== null ? String(v) : null;
    } catch {
      return null;
    }
  };

  const getPriceInfo = (product) => {
    if (!product) return null;
    const countryId = getSelectedCountryId();
    const curr = product.currencies || product.Currencies || [];
    let entry = null;
    if (countryId) entry = curr.find((c) => String(c.country_id) === String(countryId));
    if (!entry && curr.length) entry = curr[0];
    if (!entry) {
      const fallback = product.price || product.price_after_discount || null;
      return fallback ? { price: String(product.price || ""), price_after_discount: product.price_after_discount ? String(product.price_after_discount) : null } : null;
    }
    const rawPrice = entry.price != null ? String(entry.price) : null;
    const rawDiscount =
      entry.price_after_discount != null
        ? String(entry.price_after_discount)
        : null;

    const priceNum = parseFloat(rawPrice || "");
    const discountNum = parseFloat(rawDiscount || "");
    const hasValidDiscount =
      Number.isFinite(discountNum) &&
      discountNum > 0 &&
      (!Number.isFinite(priceNum) || discountNum < priceNum);

    return {
      price: rawPrice,
      price_after_discount: hasValidDiscount ? rawDiscount : null,
    };
  };

  const formatPrice = (p) => (p == null ? "" : p);

  if (loading) {
    return (
      <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
        <CircularProgress sx={{ color: "primary.main" }} size={48} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography color="error" variant="h6" fontWeight={600}>
          {t("error_loading_top_rated", { defaultValue: lang === "ar" ? "حدث خطأ في تحميل الأعلى تقييماً" : "Error loading top rated" })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        py: 8,
        px: 2,
        background: "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: "linear-gradient(90deg, #d5aa2f 0%, #e6c76a 50%, #d5aa2f 100%)",
        },
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 5,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 56,
                height: 56,
                borderRadius: 3,
                background: "linear-gradient(135deg, rgba(213, 170, 47, 0.1) 0%, rgba(230, 199, 106, 0.1) 100%)",
                border: "1px solid rgba(213, 170, 47, 0.2)",
              }}
            >
              <EmojiEventsIcon sx={{ color: "primary.main", fontSize: 32 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                fontWeight={800}
                sx={{
                  background: "linear-gradient(135deg, #d5aa2f 0%, #b08c26 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {t("top_rated_title", { defaultValue: lang === "ar" ? "الأعلى تقييماً" : "Top Rated" })}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {lang === "ar"
                  ? "المنتجات الأكثر تقييماً من عملائنا"
                  : "Most rated products from our customers"}
              </Typography>
            </Box>
          </Box>

          <Button
            component={Link}
            to="/top-rated"
            variant="outlined"
            endIcon={<ArrowForwardIcon sx={{ transform: lang === "ar" ? "rotate(180deg)" : "none" }} />}
            sx={{
              borderRadius: 2,
              borderColor: "primary.main",
              color: "primary.main",
              fontWeight: 700,
              px: 3,
              py: 1,
              transition: "all 0.3s ease",
              "&:hover": {
                bgcolor: "primary.main",
                color: "#fff",
                transform: "translateX(-4px)",
                boxShadow: "0 4px 12px rgba(213, 170, 47, 0.3)",
              },
            }}
          >
            {t("view_all", { defaultValue: lang === "ar" ? "عرض الكل" : "View all" })}
          </Button>
        </Box>

        {/* Products Grid */}
        <Grid container spacing={3}>
          {products.map((product, index) => (
            <Grid item xs={12} sm={6} md={3} key={product.id}>
              <Card
                sx={{
                  height: "100%",
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 12px 32px rgba(213, 170, 47, 0.15)",
                    "& .product-image": {
                      transform: "scale(1.1)",
                    },
                    "& .rank-badge": {
                      transform: "scale(1.1) rotate(5deg)",
                    },
                  },
                  animation: `fadeInUp 0.5s ease-out ${index * 0.1}s both`,
                  "@keyframes fadeInUp": {
                    from: {
                      opacity: 0,
                      transform: "translateY(20px)",
                    },
                    to: {
                      opacity: 1,
                      transform: "translateY(0)",
                    },
                  },
                }}
              >
                <CardActionArea
                  component={Link}
                  to={`/product/${product.id}`}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    p: 3,
                    textAlign: "center",
                    minHeight: 420,
                  }}
                >
                  {/* Rank Badge for Top 3 */}
                  {index < 3 && (
                    <Chip
                      icon={<EmojiEventsIcon sx={{ fontSize: 14 }} />}
                      label={`#${index + 1}`}
                      className="rank-badge"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        zIndex: 2,
                        bgcolor: index === 0 ? "#FFD700" : index === 1 ? "#C0C0C0" : "#CD7F32",
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: "0.75rem",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                        transition: "all 0.3s ease",
                      }}
                    />
                  )}

                  {/* Product Image */}
                  <Box
                    sx={{
                      mb: 3,
                      position: "relative",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 140,
                      overflow: "hidden",
                      borderRadius: 2,
                      bgcolor: "grey.50",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        background: "linear-gradient(180deg, transparent 50%, rgba(213, 170, 47, 0.05) 100%)",
                        pointerEvents: "none",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={getImage(product)}
                      alt={getName(product)}
                      className="product-image"
                      sx={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                        position: "relative",
                        zIndex: 1,
                      }}
                    />
                  </Box>

                  {/* Product Name */}
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    gutterBottom
                    sx={{
                      mb: 2,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      lineHeight: 1.4,
                      height: "2.8em",
                    }}
                  >
                    {getName(product)}
                  </Typography>

                  {/* Rating Display */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "rgba(213, 170, 47, 0.05)",
                      border: "1px solid rgba(213, 170, 47, 0.1)",
                    }}
                  >
                    <Rating
                      value={product.reviews_summary?.average_rating || 0}
                      precision={0.1}
                      readOnly
                      size="small"
                      sx={{
                        "& .MuiRating-iconFilled": {
                          color: "primary.main",
                        },
                      }}
                    />
                    <Typography variant="h6" fontWeight={800} color="primary.main">
                      {product.reviews_summary?.average_rating?.toFixed(1) || "0.0"}
                    </Typography>
                  </Box>

                  {/* Reviews Count */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}
                  >
                    <StarIcon sx={{ fontSize: 14, color: "primary.main" }} />
                    ({product.reviews_summary?.total_reviews || 0} {t("reviews", { defaultValue: lang === "ar" ? "تقييم" : "reviews" })})
                  </Typography>

                  {/* Price */}
                  <Box
                    sx={{
                      mt: "auto",
                      pt: 2,
                      borderTop: "1px solid rgba(0, 0, 0, 0.06)",
                    }}
                  >
                    {(() => {
                      const pinfo = getPriceInfo(product);
                      if (!pinfo) return null;
                      const orig = formatPrice(pinfo.price);
                      const disc = formatPrice(pinfo.price_after_discount);
                      return disc ? (
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              textDecoration: "line-through",
                              color: "text.disabled",
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          >
                            {orig} {lang === "ar" ? "ج.م" : "EGP"}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                            <Typography
                              variant="h5"
                              sx={{
                                color: "primary.main",
                                fontWeight: 800,
                              }}
                            >
                              {disc} {lang === "ar" ? "ج.م" : "EGP"}
                            </Typography>
                            <Chip
                              label={`-${Math.round((1 - parseFloat(disc) / parseFloat(orig)) * 100)}%`}
                              size="small"
                              sx={{
                                bgcolor: "error.main",
                                color: "#fff",
                                fontWeight: 700,
                                fontSize: "0.7rem",
                                height: 20,
                              }}
                            />
                          </Box>
                        </Box>
                      ) : (
                        <Typography
                          variant="h5"
                          sx={{
                            color: "primary.main",
                            fontWeight: 800,
                          }}
                        >
                          {orig} {lang === "ar" ? "ج.م" : "EGP"}
                        </Typography>
                      );
                    })()}
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default TopRatedSection;
